import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { useAppStore } from '../../store/useAppStore'
import { CustomField } from '../../types'

interface CloneCustomFieldsToOrganizationsModalProps {
  show: boolean
  onClose: () => void
  onSuccess?: () => void
  /** Pre-check these attribute ids when the modal opens (e.g. table selection). */
  initialSelectedFieldIds?: number[]
}

type WizardStep = 'select_attributes' | 'select_organizations' | 'result'

interface CloneResult {
  created: Array<{ source_id: number; new_id: number; organization_id: number; name: string }>
  duplicates: Array<{ attribute_id: number; attribute_name: string; organization_id: number; reason: string }>
}

const DATA_TYPE_LABELS: Record<string, string> = {
  string: 'Text',
  url: 'URL',
  date: 'Date',
  boolean: 'Boolean',
  integer: 'Number',
  float: 'Decimal',
  monetary: 'Monetary',
  documentlink: 'Document Link',
  longtext: 'Long Text',
  select: 'Selection',
  separator: 'Separator',
}

export const CloneCustomFieldsToOrganizationsModal: React.FC<CloneCustomFieldsToOrganizationsModalProps> = ({
  show,
  onClose,
  onSuccess,
  initialSelectedFieldIds,
}) => {
  const { t } = useTranslation()
  const { uiSettings } = useAppStore()

  const [step, setStep] = useState<WizardStep>('select_attributes')
  const [allFields, setAllFields] = useState<CustomField[]>([])
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(new Set())
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)
  const [result, setResult] = useState<CloneResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currentOrganizationId = uiSettings?.currentOrganizationId
  const organizations = (uiSettings?.organizations || []).filter(
    (org) => org.id !== currentOrganizationId
  )

  useEffect(() => {
    if (show) {
      const seedIds = initialSelectedFieldIds ?? []
      setStep('select_attributes')
      setSelectedFieldIds(new Set(seedIds))
      setSelectedOrgIds(new Set())
      setResult(null)
      setError(null)
      loadFields(seedIds)
    }
  }, [show, initialSelectedFieldIds])

  const loadFields = async (seedIds?: number[]) => {
    setLoadingFields(true)
    try {
      const response = await customFieldService.listAll()
      const fields = Array.isArray(response) ? response : response.results || []
      setAllFields(fields)
      const validIds = new Set(fields.map((f) => f.id))
      if (seedIds?.length) {
        setSelectedFieldIds(new Set(seedIds.filter((id) => validIds.has(id))))
      }
    } catch {
      setAllFields([])
    } finally {
      setLoadingFields(false)
    }
  }

  const toggleField = (id: number) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllFields = () => {
    if (selectedFieldIds.size === allFields.length) {
      setSelectedFieldIds(new Set())
    } else {
      setSelectedFieldIds(new Set(allFields.map((f) => f.id)))
    }
  }

  const toggleOrg = (id: number) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllOrgs = () => {
    if (selectedOrgIds.size === organizations.length) {
      setSelectedOrgIds(new Set())
    } else {
      setSelectedOrgIds(new Set(organizations.map((o) => o.id)))
    }
  }

  const handleClone = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await customFieldService.cloneToOrganizations({
        attribute_ids: Array.from(selectedFieldIds),
        organization_ids: Array.from(selectedOrgIds),
      })
      setResult(res)
      setStep('result')
      if (res.created.length > 0) {
        onSuccess?.()
      }
    } catch (err: any) {
      const data = err?.response?.data
      const detail =
        data?.code === 'clone_forbidden_full_attributes_permissions'
          ? t('clone_forbidden_full_attributes_permissions')
          : data?.detail || err?.message || t('error_saving')
      setError(detail)
    } finally {
      setLoading(false)
    }
  }

  const getOrgName = (orgId: number) => {
    return organizations.find((o) => o.id === orgId)?.name || `Org #${orgId}`
  }

  const renderStepAttributes = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold">{t('custom_fields')}</span>
        <Button variant="link" size="sm" onClick={toggleAllFields} className="p-0">
          {selectedFieldIds.size === allFields.length ? t('deselect_all') || 'Deselect all' : t('select_all')}
        </Button>
      </div>
      {loadingFields ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : allFields.length === 0 ? (
        <p className="text-muted">{t('no_items_found')}</p>
      ) : (
        <div style={{ maxHeight: 350, overflowY: 'auto' }} className="border rounded p-2">
          {allFields.map((field) => (
            <Form.Check
              key={field.id}
              type="checkbox"
              id={`clone-field-${field.id}`}
              checked={selectedFieldIds.has(field.id)}
              onChange={() => toggleField(field.id)}
              label={
                <span>
                  {field.name}{' '}
                  <Badge bg="secondary" className="ms-1">
                    {DATA_TYPE_LABELS[field.data_type] || field.data_type}
                  </Badge>
                </span>
              }
              className="mb-1"
            />
          ))}
        </div>
      )}
    </>
  )

  const renderStepOrganizations = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold">{t('organizations') || 'Organizations'}</span>
        <Button variant="link" size="sm" onClick={toggleAllOrgs} className="p-0">
          {selectedOrgIds.size === organizations.length ? t('deselect_all') || 'Deselect all' : t('select_all')}
        </Button>
      </div>
      {organizations.length === 0 ? (
        <p className="text-muted">{t('no_items_found')}</p>
      ) : (
        <div style={{ maxHeight: 350, overflowY: 'auto' }} className="border rounded p-2">
          {organizations.map((org) => (
            <Form.Check
              key={org.id}
              type="checkbox"
              id={`clone-org-${org.id}`}
              checked={selectedOrgIds.has(org.id)}
              onChange={() => toggleOrg(org.id)}
              label={org.name}
              className="mb-1"
            />
          ))}
        </div>
      )}
    </>
  )

  const renderResult = () => {
    if (!result) return null
    return (
      <>
        {result.created.length > 0 && (
          <Alert variant="success">
            <strong>{result.created.length}</strong>{' '}
            {t('attributes_cloned_successfully', {
              defaultValue: 'attribute(s) cloned successfully.',
            })}
          </Alert>
        )}
        {result.duplicates.length > 0 && (
          <Alert variant="warning">
            <strong>{result.duplicates.length}</strong> {t('skipped') || 'skipped'} ({t('duplicates') || 'duplicates'}):
            <ul className="mb-0 mt-1">
              {result.duplicates.map((dup, idx) => (
                <li key={idx}>
                  <strong>{dup.attribute_name}</strong> → {getOrgName(dup.organization_id)}: {dup.reason}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {result.created.length === 0 && result.duplicates.length === 0 && (
          <Alert variant="info">{t('no_items_found')}</Alert>
        )}
      </>
    )
  }

  const canProceedToOrgs = selectedFieldIds.size > 0
  const canClone = selectedOrgIds.size > 0

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('clone_to_organizations') || 'Clone to organizations'}
          {step === 'select_attributes' && (
            <span className="text-muted ms-2 fs-6">— {t('step') || 'Step'} 1/2</span>
          )}
          {step === 'select_organizations' && (
            <span className="text-muted ms-2 fs-6">— {t('step') || 'Step'} 2/2</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {step === 'select_attributes' && renderStepAttributes()}
        {step === 'select_organizations' && renderStepOrganizations()}
        {step === 'result' && renderResult()}
      </Modal.Body>
      <Modal.Footer>
        {step === 'select_attributes' && (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button variant="primary" disabled={!canProceedToOrgs} onClick={() => setStep('select_organizations')}>
              {t('next')}
            </Button>
          </>
        )}
        {step === 'select_organizations' && (
          <>
            <Button variant="secondary" onClick={() => setStep('select_attributes')}>
              {t('back') || 'Back'}
            </Button>
            <Button variant="primary" disabled={!canClone || loading} onClick={handleClone}>
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              {t('clone') || 'Clone'}
            </Button>
          </>
        )}
        {step === 'result' && (
          <Button variant="primary" onClick={onClose}>
            {t('close')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
